import React from "react";
import { useLocation } from "react-router-dom";
import { VideoGrid } from "./VideoGrid";
import { useSearch } from "../hooks/useSearch";
import { motion } from "framer-motion";
import { Loader2, SearchX } from "lucide-react";

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

export const Gallery: React.FC = () => {
    const query = useQuery().get("q") || "";
    const { results, loading, error, search, clearResults } = useSearch();
    const [page, setPage] = React.useState(1);
    const [hasMore, setHasMore] = React.useState(true);
    const loaderRef = React.useRef<HTMLDivElement | null>(null);
    const fetchLock = React.useRef(false);
    const PAGE_SIZE = 10;
    const initialLoading = loading && page === 1;

    // Reset and load first page when query changes
    React.useEffect(() => {
        setPage(1);
        setHasMore(true);
        clearResults();
        if (query) {
            fetchLock.current = true;
            search(query, 1, PAGE_SIZE, false).then((res) => {
                fetchLock.current = false;
                if (!res || res.results.length < PAGE_SIZE) {
                    setHasMore(false);
                }
            });
        }
    }, [query, search, clearResults]);

    // Infinite scroll observer
    React.useEffect(() => {
        if (!hasMore) return;
        const node = loaderRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting && !loading && !fetchLock.current) {
                    fetchLock.current = true;
                    search(query, page + 1, PAGE_SIZE, true).then((res) => {
                        fetchLock.current = false;
                        if (!res || res.results.length < PAGE_SIZE) {
                            setHasMore(false);
                        } else {
                            setPage((p) => p + 1);
                        }
                    });
                }
            },
            { threshold: 0.3 },
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loading, query, page, search]);

    return (
        <div className="w-full flex flex-col gap-8 min-h-[50vh]">
            {error && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-destructive/10 border border-destructive/20 text-destructive px-6 py-4 rounded-xl w-full text-center max-w-2xl mx-auto"
                >
                    <p className="font-medium">Unable to load results</p>
                    <p className="text-sm opacity-80 mt-1">{error}</p>
                </motion.div>
            )}

            {results && (
                <div className="w-full flex flex-col gap-8">
                    <VideoGrid
                        videos={results.results}
                        loading={initialLoading}
                    />

                    {hasMore && (
                        <div
                            ref={loaderRef}
                            className="h-24 flex items-center justify-center w-full"
                        >
                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                {!initialLoading && loading ? (
                                    <>
                                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                        <span className="text-sm">
                                            Loading more moments...
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-sm opacity-50">
                                        Scroll for more
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {!results && !loading && !error && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center py-20 text-center space-y-6"
                >
                    <div className="w-24 h-24 rounded-full bg-muted/30 flex items-center justify-center">
                        <SearchX className="w-10 h-10 text-muted-foreground/50" />
                    </div>
                    <div className="space-y-2 max-w-md">
                        <h2 className="text-2xl font-semibold">
                            Ready to search
                        </h2>
                        <p className="text-muted-foreground">
                            Enter a description of the video moment you're
                            looking for, like "cat jumping" or "sunset over the
                            ocean".
                        </p>
                    </div>
                </motion.div>
            )}

            {initialLoading && (
                <div className="w-full h-64 flex items-center justify-center">
                    <Loader2 className="w-10 h-10 animate-spin text-primary/50" />
                </div>
            )}
        </div>
    );
};
